import { faEye, faEyeSlash, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, useFormikContext } from "formik";
import React, { FC, useState } from "react";
import { toast } from "react-toastify";
import Togglebox from "../../components/togglebox/togglebox";
import { COMMAND_STATE } from '../../constants/command-status';
import Commands from '../../constants/commands';
import { AccountSubCmds } from '../../constants/sub-commands';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Button } from '../../components/button/Button';
import { useLocale } from "../../contexts/localeContext";
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { executeCommand } from '../../services/executeCommandService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { validateItemName, validatePassword } from "../../utilities/Validation";
import { useDialog } from "../dialog/DialogContext";
import Label from "../label/label";
import Tooltip from "../Tooltip/Tooltip";

interface STidMobileAccountCardProps {
    isLicensed: boolean,
    currentItem: any,
    isAccountPopulated: boolean, // Whether all the required details are populated to allow querying
}

const STidMobileAccountCard: FC<STidMobileAccountCardProps> = ({ isAccountPopulated, isLicensed, currentItem }) => {
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();
    const [showUserId, setShowUserId] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    const { dirty } = useFormikContext<any>();
    const errorHandler = (accessor) => {
        const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }, errors as any);
        if (error) {
            return <span className='text-red-600 text-xs -ml-3 pr-8'>{error}</span>
        } else {
            return null;
        }
    };

    /**
    * The account is queried using saved account details only. Therefore, to query the account:
    * 1. Must be licensed.
    * 2. The account details must be populated.
    * 3. There must be no unsaved changes.
    * @returns 
    */
    const canTestAccount = () => {
        return isLicensed && isAccountPopulated && !dirty;
    }

    const onEnabled = () => {
        const newEnabledState = !values.enabled;

        if (newEnabledState && !isLicensed) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unlicensed), message: translate.byKey(TranslationKeys.unlicensedFeature) });
            setFieldValue("enabled", false);
            return;
        }

        setFieldValue("enabled", newEnabledState);
    }

    const testAccount = () => {
        setBusy(true);
        const command: ExecuteCommand = {
            cmd: Commands.Account.label,
            cmdSubType: AccountSubCmds.Test_Account,
            entityId: currentItem.id,
            jsonResponse: ""
        }

        executeCommand(command).then((response: any) => {
            if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                if (values.enabled === false) {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToDisabledAccount));
                }
                else {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToAccount));
                }
            }
            else {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.testAccountFailed) });
            }
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    const onValidatePassword = (value: string) => {

        // Ignores validation only if password is already captured and no new edits are made
        if (currentItem?.isPasswordCaptured && !value) {
            return null;
        }

        // If this is either the initial capture of the password or an edit, validate it
        return validatePassword(value, translate, currentItem, 2);
    }

    return <div className="w-full p-2 inline-flex justify-start items-top bg-white">
        <div className="flex flex-col py-4 px-8">
            <div className="mb-2">
                <div className="flex flex-row justify-between" id="userIdLabel">
                    <div className="flex items-center gap-1">
                        <Label htmlFor="details.userId.value" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.apiClientId)}:</Label>
                        <Tooltip text={translate.byKey(TranslationKeys.apiClientIdTooltip)} direction="bottom">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 cursor-help text-xs" />
                        </Tooltip>
                    </div>
                    {errorHandler("details.userId.value")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='userIdInputFieldId'
                        name="details.userId.value"
                        type={showUserId ? 'text' : 'password'}
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-56 bg-gray-input"
                        validate={(value) => validateItemName(value, translate)} />
                    <div className="flex relative w-6 ml-1">
                        <FontAwesomeIcon
                            className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                            onClick={() => setShowUserId(showUserId => !showUserId)}
                            icon={showUserId ? faEye : faEyeSlash} />
                    </div>
                </div>
            </div>

            <div>
                <div className="flex flex-row justify-between mb-1" id="passwordLabel">
                    <div className="flex items-center gap-1">
                        <Label
                            htmlFor="tempPassword"
                            className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.apiKey)}:</Label>
                        <Tooltip text={translate.byKey(TranslationKeys.apiKeyTooltip)} direction="right">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 cursor-help text-xs" />
                        </Tooltip>
                    </div>
                    {errorHandler("tempPassword")}
                </div>
                <div className="flex flex-row justify-between">
                    <Field id='passwordInputFieldId'
                        type={showPassword ? 'text' : 'password'}
                        name="tempPassword"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-56 bg-gray-input"
                        validate={(value) => onValidatePassword(value)} 
                        placeholder={(currentItem?.isPasswordCaptured) ? "********" : translate.byKey(TranslationKeys.enterNewPassword)}/>
                    <div className="flex relative w-6 ml-1">
                        <FontAwesomeIcon
                            className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                            onClick={() => setShowPassword(showPassword => !showPassword)}
                            icon={showPassword ? faEye : faEyeSlash} />
                    </div>
                </div>
            </div>
        </div>
        <div className="flex flex-col py-4 px-8">
            <div className="mb-2">
                <div className="flex flex-row justify-between" id="enabled">
                    <Label htmlFor="enabled" className="text-xs text-gray-600 mt-1">
                        {translate.byKey(TranslationKeys.enabled)}:
                    </Label>
                </div>
                <Togglebox name="enabled" onChanged={onEnabled} label={""} />
            </div>

            {/* Test the account is setup correctly */}
            <div className="flex w-full py-4">
                <Button
                    variant="tertiary"
                    shape="rounded"
                    className="h-8 px-2 mt-1"
                    disabled={!isLicensed || !canTestAccount()}
                    onClick={(e) => testAccount()}
                    id="testEmailSettingsButton">
                    <span className="mr-2">{translate.byKey(TranslationKeys.testAccount)}</span>
                </Button>
            </div>
        </div>
    </div>
}

export default STidMobileAccountCard;
